/****************************************************
 * Module Name:  mixViewStockMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Avatar, Box, Chip, Container, Tooltip, Typography, Button } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Constants from "../../utility/constants";
import AddUserModal from "../master/addUserModal";
import UserProxy from "../../utility/proxy/manager/userProxy";
import CommonSnackbar from "../common/commonSnackBar";
import MixDiamondViewStockBarChart from "../dashboard/mixDiamondViewStockBarChart";
import NoPermission from "../common/noPermission";
import { useEffect } from "react";
import Utility from "../../utility/utility";
import BucketDetailModal from "./bucketDetailModal";
import PageTitle from "../common/pageTitle";

const MixViewStockMaster = () => {
  const [data, setData] = useState([]);
  const [columnNames, setColumnNames] = useState([]);
  const [searchCriteria, setSearchCriteria] = useState(false);
  const [showBucketLoadSpinner, setShowBucketLoadSpinner] = useState(false);
  const [userId, setUserId] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showBucketDetailModal, setShowBucketDetailModal] = useState(false);
  const [bucketId, setBucketId] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [access, setAccess] = useState(false);

  useEffect(() => {
    PageTitle("MIX_REPORT_VIEW_STOCK");
    checkPermission();
  }, []);

  const checkPermission = () => {
    let allowed = Constants.USER_PERMISSION.find(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.MIX_VIEW_STOCK)
    if (!allowed) {
      setAccess(true)
      return <NoPermission />
    }
  }

  useEffect(() => {
    const filterMap = generateFilterMap(Array.isArray(data) ? data : [data]);

    const uniqueFilterValues = {
      shape: Utility.getDistinctOptions(data, "shape"),
      size: Utility.getDistinctOptions(data, "size"),
      color: Utility.getDistinctOptions(data, "color"),
      clarity: Utility.getDistinctOptions(data, "clarity"),
      stonetype: Utility.getDistinctOptions(data, "stonetypefullname"),
    };

    prepareColumns(uniqueFilterValues, filterMap);
  }, [data]);

  const generateFilterMap = (responseData) => {
    const filterMap = {};

    // Process min alter username
    const minAlterUsername = responseData.flatMap((user) =>
      user.minalertusername.map((u) => ({
        id: u.id,
        username: u.username,
      }))
    );
    filterMap.minalertusername = [
      ...new Set(
        minAlterUsername.map((user) => `${user.id} | ${user.username}`)
      ),
    ];

    // Process max alter username
    const maxAlertUsername = responseData.flatMap((user) =>
      user.maxalertusername.map((u) => ({
        id: u.id,
        username: u.username,
      }))
    );
    filterMap.maxalertusername = [
      ...new Set(
        maxAlertUsername.map(
          (user) => `${user.id} | ${user.username}`
        )
      ),
    ];

    return filterMap;
  };

  const handleBucketDetailModal = (params) => {
    setShowBucketDetailModal(true);
    setBucketId(params.value);
  };

  const prepareColumns = (uniqueFilterValues, filterMap) => {
    let columnNames = [];
    Constants.USER_COLUMN_PERMISSION.forEach(iColumnPermission => {
      if (iColumnPermission === "f12c1ec3-2901-4368-ac5f-664720c5600c") {
        columnNames.push(
          {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 110,
            renderCell: (params) => (Utility.NeedToShowBucketDetailModal()) ?
              <Button
                sx={{
                  textDecorationLine: "underline",
                  color: 'black'
                }}
                onClick={() => handleBucketDetailModal(params)}
              >
                {params.value}
              </Button>
              :
              <div>{params.value}</div>,
          },
        );
      }
    })

    columnNames.push(...initialColumns(uniqueFilterValues));

    Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
      if (columnsData.hasOwnProperty(iColPermission)) {
        const columnData = columnsData[iColPermission];
        if (columnData.field === "minalertusername") {
          columnData.type = "singleSelect";
          columnData.valueOptions = filterMap ? filterMap.minalertusername : [];
          columnData.filterOperators = Utility.customFilters(filterMap, columnData.field, 'id', 'username');
        }
        if (columnData.field === "maxalertusername") {
          columnData.type = "singleSelect";
          columnData.valueOptions = filterMap ? filterMap.maxalertusername : [];
          columnData.filterOperators = Utility.customFilters(filterMap, columnData.field, 'id', 'username');
        }
        columnNames.push(columnData);
      }
    });

    setColumnNames(columnNames);
  }
  const columnsData = {
    "45e76150-8506-4e5c-9e00-04812830404b": {
      field: "minalertqty",
      headerName: "MIN ALERT QTY",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 200,
      renderCell: renderCellExpand,
    },
    "e38095be-101d-45ed-9a10-aef1aba03f8c": {
      field: "maxalertqty",
      headerName: "MAX ALERT QTY",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 200,
      renderCell: renderCellExpand,
    },
    "7b076812-4af4-462e-abcb-1eee8f44d740": {
      field: "minalertusername",
      headerName: "MIN ALERT USERNAME",
      headerAlign: "center",
      align: "center",
      width: 270,
      valueGetter: (params) => params.value,
      renderCell: (params) => (
        <Box sx={{ minHeight: "auto", mt: 1 }}>
          {params.value.map((item, index) => (
            <Chip
              key={index}
              avatar={<Avatar>{item.id}</Avatar>}
              label={item.username}
              onClick={() => {
                setUserId(item.id);
                setShowAddUserModal(true);
                setLoadingButton(false);
              }}
              sx={{ margin: "0px 10px 10px 0px" }}
            />
          ))}
        </Box>
      ),
      disableExport: true,
      sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "username")
    },
    "5a220faa-49a0-403b-8c1a-2e001d7721be": {
      field: "maxalertusername",
      headerName: "MAX ALERT USERNAME",
      headerAlign: "center",
      align: "center",
      width: 270,
      valueGetter: (params) => params.value,
      renderCell: (params) => (
        <Box sx={{ minHeight: "auto", mt: 1 }}>
          {params.value.map((item, index) => (
            <Chip
              key={index}
              avatar={<Avatar>{item.id}</Avatar>}
              label={item.username}
              onClick={() => {
                setUserId(item.id);
                setShowAddUserModal(true);
                setLoadingButton(false);
              }}
              sx={{ margin: "0px 10px 10px 0px" }}
            />
          ))}
        </Box>
      ),
      disableExport: true,
      sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "username")
    },
    "809a4ec3-4c31-4077-b96d-97d53d57efac": {
      field: "avgprice",
      headerName: "AVG PRICE",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 95,
      renderCell: renderCellExpand,
    },
    "03c60e42-31d0-4b12-b8f0-d7e141ae6c28": {
      field: "qty",
      headerName: "QTY",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 95,
      renderCell: renderCellExpand,
    }
  }

  const initialColumns = (combinedFilterValues) => [
    {
      field: "shape",
      headerName: "SHAPE",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.shape : [],
      filterOperators: Utility.combinedFilters(),
      width: 100,
    },
    {
      field: "size",
      headerName: "SIZE",
      headerAlign: "center",
      align: "right",
      width: 100,
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.size : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand
    },
    {
      field: "clarity",
      headerName: "CLARITY",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.clarity : [],
      filterOperators: Utility.combinedFilters(),
      width: 95,
      renderCell: renderCellExpand,
    },
    {
      field: "color",
      headerName: "COLOR",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.color : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
      width: 95,
    },
    {
      field: "stonetypefullname",
      headerName: "STONETYPE",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: combinedFilterValues ? combinedFilterValues.stonetype : [],
      filterOperators: Utility.combinedFilters(),
      width: 95,
      renderCell: renderCellExpand,
    },
  ];

  const handleValidation = (bucketData) => {
    //TODO do validation if needed here
    return true;
  };

  const hideAddUserModal = () => {
    setShowAddUserModal(false);
  };
  const hideAddBucketModal = () => {
    setShowBucketDetailModal(false);
  };

  const editUser = (userData) => {
    setLoadingButton(true);
    UserProxy.UpdateUser(userData, (responseData) => {
      hideAddUserModal();
      setLoadingButton(false);
    });
  };

  const rows = data;
  const columns = columnNames;
  return (
    <div>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Error"}
        handleClose={() => setErrorSnackbar(false)}
      />
      {showAddUserModal ? (
        <AddUserModal
          Show={true}
          handleClose={hideAddUserModal}
          handleSave={editUser}
          handleValidation={handleValidation}
          editOperation={false}
          sourceData={undefined}
          userId={userId}
          loading={loadingButton}
          saveButton={true}
        />
      ) : (
        <Box />
      )}
      {showBucketDetailModal ? (
        <BucketDetailModal
          show={showBucketDetailModal}
          bucketId={bucketId}
          handleClose={hideAddBucketModal}
        />
      ) : null}
      {
        access ? <NoPermission /> : (
          <div>

            {/* <Box
              style={{ textAlign: "center", fontSize: "xx-large", marginTop: "30px" }}
            >
              Stock List
            </Box> */}
            <Box className="background">
              <Box style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '20px'
              }}>
                <Box
                  style={{
                    width: '55%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    textAlign: "center",
                    fontSize: "xx-large",

                  }}
                >
                  <Typography id="glass" style={{
                    textAlign: "center",
                    width: '250px',
                    color: '#fff',
                    fontSize: "xx-large",
                  }}>

                    Mix Stock List
                  </Typography>
                </Box>
              </Box>
              <Container maxWidth="xxl" sx={{ mt: 1 }} >
                <MixDiamondViewStockBarChart
                  searchCriteria={searchCriteria}
                  setSearchCriteria={setSearchCriteria}
                  setDataOfMaster={setData}
                  masterLoader={setShowBucketLoadSpinner}
                  apiCrashMasterError={errorSnackbar}
                />
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                      marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                      fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                      fontWeight: Constants.defaultFontWeight,
                    },
                    height: "80vh",
                    mt: 2,
                  }}
                >
                  <DataGridPro
                    disableRowSelectionOnClick={true}
                    getRowHeight={() => "auto"}
                    sx={{
                      '& .MuiDataGrid-columnHeader': {
                        color: '#fff',
                        backgroundColor: 'grey',
                      },
                      "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                        py: "8px",
                      },
                      "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                        py: "15px",
                      },
                      "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                        py: "22px",
                      },
                      fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                    }}
                    density="compact"
                    slots={{ toolbar: GridToolbar }}
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    initialState={{
                      sorting: {
                        sortModel: [{ field: "id", sort: "desc" }],
                      },
                      pagination: {
                        paginationModel: Constants.pageSize,
                      },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    loading={showBucketLoadSpinner}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                      },
                    }}
                  />
                  <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                    {showBucketLoadSpinner ? (
                      <CircularProgress size="1rem" />
                    ) : (
                      <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                          color="primary"
                          fontSize="medium"
                          onClick={() => setSearchCriteria(true)}
                          sx={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Container>
            </Box>
          </div>
        )
      }
    </div>
  );
};

export default MixViewStockMaster;
